import { AuthChangeEvent, AuthError, Session } from '@supabase/supabase-js'
import { supabase } from './supabase'

/**
 * Sends a 6-digit OTP code to the given email via the Supabase Auth OTP code flow.
 * The user will receive the code and type it into the sign-in screen in this PWA,
 * not click a magic link that opens in their mail client.
 *
 * @param email - The email to send the OTP to
 * @returns A promise resolving to `{ error: AuthError | null }`; `null` on success
 */
export async function sendOtp(email: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signInWithOtp({ email })
  return { error }
}

/**
 * Verifies the 6-digit OTP code sent to the user's email.
 * On success, establishes a session; the caller doesn't need the session object back.
 * Auth state change is observed via `onAuthStateChange` and the SessionProvider.
 *
 * @param email - The email the OTP was sent to
 * @param token - The 6-digit code the user received
 * @returns A promise resolving to `{ error: AuthError | null }`; `null` on success
 */
export async function verifyOtp(
  email: string,
  token: string,
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
  return { error }
}

/**
 * Initiates OAuth sign-in with the given provider.
 * Per the plan, Google and Microsoft buttons are wired but dark this phase.
 * This function exists so SignInScreen can call it once a config flag lights a button up.
 *
 * @param provider - `'google'` or `'azure'` (Microsoft/Entra);
 *                   `'azure'` is Supabase's literal provider id for Microsoft
 * @returns A promise resolving to `{ error: AuthError | null }`; the SDK handles browser redirect internally
 */
export async function signInWithOAuth(
  provider: 'google' | 'azure',
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: `${window.location.origin}/auth/callback` },
  })
  return { error }
}

/**
 * Signs out the current user.
 *
 * @returns A promise resolving to `{ error: AuthError | null }`; `null` on success
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signOut()
  return { error }
}

/**
 * Subscribes to auth state changes.
 * This is a thin pass-through to the Supabase SDK's onAuthStateChange.
 * Used by SessionProvider and other modules that need to observe sign-in/out.
 *
 * @param callback - Called with `(event, session)` whenever auth state changes.
 *                   Use the `data.subscription.unsubscribe()` method in useEffect cleanup.
 * @returns The subscription object from the SDK with `.data.subscription.unsubscribe()`
 */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void,
) {
  return supabase.auth.onAuthStateChange(callback)
}
